/**
 * Multi-Location Inventory Integration for Shopify
 * Displays product availability across franchise locations
 */

class InventoryIntegration {
  constructor() {
    this.apiBaseUrl = window.inventoryConfig?.apiUrl || 'http://localhost:3002/api/inventory';
    this.enabledStores = window.inventoryConfig?.enabledStores || ['Apple Valley', 'Grand Blanc', 'Mainstream Test Website'];
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    
    this.init();
  }

  init() {
    // Check if inventory system is disabled
    if (window.inventoryConfig?.disabled) {
      console.log('🚫 Inventory system disabled');
      return;
    }
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.processProducts());
    } else {
      this.processProducts();
    }
  }

  async processProducts() {
    const productBlocks = document.querySelectorAll('product-block[data-product-id]');
    
    if (productBlocks.length === 0) return;

    // Process products in batches to avoid overwhelming the API
    const batchSize = 10;
    for (let i = 0; i < productBlocks.length; i += batchSize) {
      const batch = Array.from(productBlocks).slice(i, i + batchSize);
      await this.processBatch(batch);
      
      // Small delay between batches
      if (i + batchSize < productBlocks.length) {
        await this.delay(100);
      }
    }
  }

  async processBatch(productBlocks) {
    const promises = productBlocks.map(block => this.addInventoryDisplay(block));
    await Promise.all(promises);
  }

  async addInventoryDisplay(productBlock) {
    const productId = productBlock.dataset.productId;
    
    try {
      // Get product handle from the product link
      const productLink = productBlock.querySelector('.product-link');
      if (!productLink) return;
      
      const productUrl = productLink.getAttribute('href');
      const productHandle = this.extractHandleFromUrl(productUrl);
      
      if (!productHandle) return;

      // Create inventory container
      const inventoryContainer = this.createInventoryContainer();
      
      // Insert after the price element
      const priceElement = productBlock.querySelector('.product-price');
      if (priceElement) {
        priceElement.parentNode.insertBefore(inventoryContainer, priceElement.nextSibling);
      } else {
        // Fallback: insert at end of product details
        const detailContainer = productBlock.querySelector('.product-block__detail .inner .innerer');
        if (detailContainer) {
          detailContainer.appendChild(inventoryContainer);
        }
      }

      // Load inventory data
      await this.loadInventoryForProduct(productHandle, inventoryContainer);
      
    } catch (error) {
      console.warn('Failed to add inventory display for product:', productId, error);
    }
  }

  createInventoryContainer() {
    const container = document.createElement('div');
    container.className = 'product-inventory-availability';
    container.innerHTML = `
      <div class="inventory-loading">
        <span>Checking availability...</span>
      </div>
    `;
    return container;
  }

  async loadInventoryForProduct(productHandle, container) {
    try {
      const availability = await this.getProductAvailability(productHandle);
      this.updateInventoryDisplay(container, availability);
    } catch (error) {
      console.warn('Failed to load inventory for:', productHandle, error);
      this.showInventoryError(container);
    }
  }

  async getProductAvailability(productHandle) {
    // Check cache first
    const cacheKey = productHandle;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/search?handle=${encodeURIComponent(productHandle)}&available_only=true`);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: data,
        timestamp: Date.now()
      });
      
      return data;
      
    } catch (error) {
      console.warn('API request failed:', error);
      return { products: [] };
    }
  }

  updateInventoryDisplay(container, availability) {
    if (!availability.products || availability.products.length === 0) {
      container.innerHTML = `
        <div class="inventory-unavailable">
          <span class="inventory-icon">📦</span>
          <span>Not available online</span>
        </div>
      `;
      return;
    }

    const product = availability.products[0];
    const availableStores = [];
    
    // Check each store for availability
    this.enabledStores.forEach(storeName => {
      const storeData = product.stores && product.stores[storeName];
      if (storeData && storeData.inventory_quantity > 0) {
        availableStores.push({
          name: storeName,
          quantity: storeData.inventory_quantity
        });
      }
    });

    if (availableStores.length === 0) {
      container.innerHTML = `
        <div class="inventory-unavailable">
          <span class="inventory-icon">📦</span>
          <span>Out of stock</span>
        </div>
      `;
    } else if (availableStores.length === 1) {
      container.innerHTML = `
        <div class="inventory-available inventory-single">
          <span class="inventory-icon">✅</span>
          <span>Available at ${availableStores[0].name}</span>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="inventory-available inventory-multiple">
          <span class="inventory-icon">✅</span>
          <span>Available at ${availableStores.length} locations</span>
          <button class="inventory-details-btn" type="button">View details</button>
        </div>
        <div class="inventory-details-popup" style="display: none;">
          <div class="inventory-popup-content">
            <h4>Available at:</h4>
            ${availableStores.map(store => 
              `<div class="inventory-store">
                <strong>${store.name}</strong>
                <span class="inventory-qty">${store.quantity > 5 ? '5+' : store.quantity} in stock</span>
              </div>`
            ).join('')}
            <button class="inventory-close-btn" type="button">Close</button>
          </div>
        </div>
      `;

      // Add event listeners for popup
      this.addPopupListeners(container);
    }
  }

  addPopupListeners(container) {
    const detailsBtn = container.querySelector('.inventory-details-btn');
    const closeBtn = container.querySelector('.inventory-close-btn');
    const popup = container.querySelector('.inventory-details-popup');

    if (detailsBtn && popup) {
      detailsBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        popup.style.display = 'block';
      });
    }

    if (closeBtn && popup) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        popup.style.display = 'none';
      });
    }

    // Close on click outside
    if (popup) {
      popup.addEventListener('click', (e) => {
        if (e.target === popup) {
          popup.style.display = 'none';
        }
      });
    }
  }

  showInventoryError(container) {
    container.innerHTML = `
      <div class="inventory-error">
        <span class="inventory-icon">⚠️</span>
        <span>Availability unavailable</span>
      </div>
    `;
  }

  extractHandleFromUrl(url) {
    if (!url) return null;
    
    // Handle relative URLs
    if (url.startsWith('/')) {
      const parts = url.split('/');
      const productIndex = parts.indexOf('products');
      if (productIndex !== -1 && parts[productIndex + 1]) {
        return parts[productIndex + 1];
      }
    }
    
    return null;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  // Allow configuration via global object
  window.inventoryConfig = window.inventoryConfig || {};
  
  // Initialize the inventory integration
  new InventoryIntegration();
}